import { Extension } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

const MINUTE_NODE_INTERNAL_TYPE = 'MinuteNode'

type TitleRange = { from: number; to: number }

/** The range of each MinuteNode's title paragraph (its first child), across the whole document. */
function titleRanges(doc: ProseMirrorNode): TitleRange[] {
  const ranges: TitleRange[] = []
  doc.descendants((node, pos) => {
    if (node.type.name === 'listItem' && node.attrs?.['internalType'] === MINUTE_NODE_INTERNAL_TYPE) {
      const firstChild = node.firstChild
      if (firstChild !== null && firstChild.type.name === 'paragraph') {
        ranges.push({ from: pos + 1, to: pos + 1 + firstChild.nodeSize })
      }
    }
    return true
  })
  return ranges
}

/**
 * Makes each agenda item's title paragraph read-only while its content stays editable. Used for a Restricted user,
 * who may edit the body of an item shared with them but not rename it. Implemented at the view layer (a
 * `contenteditable=false` node decoration plus a local text-input guard) so it never blocks the remote Y.js sync
 * transactions that collaboration depends on.
 */
export const ProtectMinuteTitles = Extension.create({
  name: 'protectMinuteTitles',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('protectMinuteTitles'),
        props: {
          decorations(state) {
            const decorations = titleRanges(state.doc).map((range) => Decoration.node(range.from, range.to, { contenteditable: 'false' }))
            return DecorationSet.create(state.doc, decorations)
          },
          handleTextInput(view, from, to) {
            return titleRanges(view.state.doc).some((range) => from >= range.from && to <= range.to)
          },
        },
      }),
    ]
  },
})
